import React from 'react'
import { useQuery } from '@tanstack/react-query'
import * as yup from 'yup'
import { User } from '@matech/thebigpos-sdk'
import { useIntl } from 'react-intl'
import { SelectChangeEvent } from '@mui/material'

import { TheBigPOSApi } from '../lib/TheBigPOSClient'
import { Button } from '../components/Button'
import { Form, FormRow, Select } from '../components/formControls'
import useForm from '../hooks/useForm'

export type GenerateLoanDocumentFormFieldValues = {
	customDocumentTemplateId: string
	notificationOption: string
	recipients: string[]
}

export type GenerateLoanDocumentFormProps = {
	type: string
	borrowers: User[]
	onCancel: () => void
	onSubmit: (data: GenerateLoanDocumentFormFieldValues) => void
}

const GenerateLoanDocumentForm = ({
	type,
	borrowers,
	onCancel,
	onSubmit,
}: GenerateLoanDocumentFormProps) => {
	const { data: customDocumentTemplates } = useQuery({
		queryKey: ['customDocumentTemplates', type],
		queryFn: () => TheBigPOSApi.getCustomDocumentTemplates(type),
	})

	const { formatMessage } = useIntl()

	const notificationOptions = [
		{
			label: formatMessage({
				id: 'generate_loan_document.dont_send',
			}),
			value: 'dont_send',
		},
		{
			label:
				borrowers && borrowers.length > 1
					? formatMessage({
							id: 'generate_loan_document.send_to_all_borrowers',
						})
					: formatMessage(
							{
								id: 'generate_loan_document.send_to_borrower',
							},
							{
								name: `${borrowers[0]?.firstName} ${borrowers[0]?.lastName}`,
							}
						),
			value: 'send_all',
		},
	]

	if (borrowers.length > 1) {
		notificationOptions.push({
			label: formatMessage({
				id: 'generate_loan_document.send_to_specific_borrowers',
			}),
			value: 'send_list',
		})
	}

	const schema = yup.object().shape({
		customDocumentTemplateId: yup.string().required(
			formatMessage({
				id: 'form_validations.select_document_template',
			})
		),
		notificationOption: yup.string().required(
			formatMessage({
				id: 'form_validations.notification_option_required',
			})
		),
		recipients: yup.array().when('notificationOption', {
			is: 'dont_send',
			then: (schema) =>
				schema.length(
					0,
					formatMessage({
						id: 'form_validations.empty_when_dont_send_selected',
					})
				),
			otherwise: (schema) =>
				schema.min(
					1,
					formatMessage({
						id: 'form_validations.at_least_one_borrower_selected',
					})
				),
		}),
	})

	const {
		handleSubmit,
		watch,
		control,
		formState: { errors },
		setValue,
	} = useForm<GenerateLoanDocumentFormFieldValues>({
		defaultValues: {
			customDocumentTemplateId: '',
			notificationOption: notificationOptions[0].value,
			recipients: [],
		},
		schema,
	})

	const selectedOption = watch('notificationOption')

	const handleNotificationOptionChanged = (
		event: SelectChangeEvent<unknown>
	) => {
		if (event.target?.value === 'send_all') {
			setValue(
				'recipients',
				borrowers.map((borrower) => borrower.id)
			)
			return
		}

		setValue('recipients', [])
	}

	const handleSelectedOptionChanged = (
		event: SelectChangeEvent<unknown>
	) => {
		if (typeof event.target.value === 'string')
			setValue('recipients', [event.target.value], {
				shouldValidate: true,
			})
	}

	return (
		<div>
			{customDocumentTemplates?.data &&
				customDocumentTemplates.data.length > 0 && (
					<Form onSubmit={handleSubmit(onSubmit)}>
						<FormRow>
							<Select
								label={formatMessage({
									id: 'form_validations.select_document_template',
								})}
								name="customDocumentTemplateId"
								menuItems={customDocumentTemplates.data.map(
									(option) => ({
										value: option.id,
										label: option.name,
									})
								)}
								fullWidth
								control={control}
								errors={errors}
							/>
						</FormRow>
						{borrowers.length > 0 && (
							<FormRow>
								<Select
									label={formatMessage({
										id: 'generate_loan_document.notification_option',
									})}
									name="notificationOption"
									menuItems={notificationOptions.map((option) => ({
										value: option.value,
										label: option.label,
									}))}
									fullWidth
									selectProps={{
										onChange: handleNotificationOptionChanged,
									}}
									control={control}
									errors={errors}
								/>
							</FormRow>
						)}

						{selectedOption === 'send_list' && (
							<FormRow>
								<Select
									label={formatMessage({
										id: 'generate_loan_document.select_borrowers',
									})}
									name="recipients"
									menuItems={borrowers.map((borrower) => ({
										value: borrower.id,
										label: `${borrower.firstName} ${borrower.lastName}`,
									}))}
									selectProps={{
										multiple: true,
										onChange: handleSelectedOptionChanged,
									}}
									fullWidth
									control={control}
									errors={errors}
								/>
							</FormRow>
						)}
						<FormRow className="mt-6 flex flex-col items-center gap-3">
							<Button
								className="!mx-auto"
								fullWidth
								type="submit"
								text={formatMessage({
									id: 'generate_loan_document.generate_button_text',
								})}
								style={{ width: 280 }}
							/>
							<Button
								className="!mx-auto"
								fullWidth
								onClick={onCancel}
								text="Cancel"
								style={{ width: 280 }}
								variant="outlined"
							/>
						</FormRow>
					</Form>
				)}
		</div>
	)
}

export default GenerateLoanDocumentForm
